import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../../shared/hooks/api";
import { error, success } from "../../../utils/toast";
import BackButton from "../../../shared/components/BackButton";
import PageLoader from "../../../shared/components/LoadingPage";
import apiMethods from "../../../utils/api";
import { ExtraFeeDetailWrapper } from "./Styled";
import Title from "./Title";
import FormContainer from "./FormContainer";
import CancelModal from "../../../shared/components/CancelModal";
import { WarningUnsavedChanges } from "../../../configs/warningProductModal";

const ExtraFeeDetail = () => {
    const { extraFeeId } = useParams();
    const navigate = useNavigate();
    const [validated, setValidated] = useState(false);
    const [isModified, setIsModified] = useState(false);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [navigationPath, setNavigationPath] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const [
        { data, isLoading, setLocalData, setLocalUrl: setGetUrl },
        fetchextraFee,
    ] = api.get(`/extrafees/${extraFeeId}/`);

    const extraFeeData = data;

    useEffect(() => {
        setGetUrl(`/extrafees/${extraFeeId}/`);
    }, [extraFeeId, setGetUrl]);

    // Use a direct flag for modifications
    const markAsModified = useCallback(() => {
        setIsModified(true);
    }, []);

    // Add beforeunload event listener for browser navigation
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isModified) {
                e.preventDefault();
                e.returnValue = "";
                return "";
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isModified]);

    const handleUpdate = async () => {
        if (isSaving) return;
        setIsSaving(true);
        try {
            await apiMethods.patch(`/extrafees/${extraFeeId}/`, data);
            await fetchextraFee();
            setIsModified(false);
            success("THÀNH CÔNG", "Cập nhật chi phí thành công");
        } catch (err) {
            error("Lỗi cập nhật chi phí", err);
        } finally {
            setIsSaving(false);
        }
    };

    const confirmNavigation = useCallback((path: string) => {
        if (isModified) {
            setNavigationPath(path);
            setShowUnsavedDialog(true);
            return false;
        }
        
        navigate(path);
        return true;
    }, [isModified, navigate]);

    const handleBackClick = () => {
        confirmNavigation("/extraFees/");
    };

    const handleSaveAndNavigate = async () => {
        setShowUnsavedDialog(false);
        try {
            await handleUpdate();
            navigate(navigationPath);
        } catch (err) {
            error("Lỗi cập nhật chi phí", err);
        }
    };

    const handleDontSaveAndNavigate = () => {
        setShowUnsavedDialog(false);
        navigate(navigationPath);
    };

    const handleContinueEditing = () => {
        setShowUnsavedDialog(false);
    };

    const handleDataChange = useCallback((updateExtraFee: any) => {
        // Mark as modified on ANY data change
        markAsModified();
        setLocalData({
            ...data,
            ...updateExtraFee,
        });
    }, [data, setLocalData, markAsModified]);

    return (
        <ExtraFeeDetailWrapper>
            <BackButton 
                label="Trở Về" 
                onClick={handleBackClick}
            />
            {isLoading && <PageLoader />}

            {!isLoading && (
                <Title
                    title={extraFeeData.name}
                    data={extraFeeData}
                    setValidated={setValidated}
                    onSubmit={handleUpdate}
                    onDataChange={markAsModified}
                    hasUnsavedChanges={isModified}
                    onCancelClick={() => confirmNavigation("/extraFees/")}
                    isLoading={isSaving}
                />
            )}

            {!isLoading && (
                <FormContainer
                    validated={validated}
                    extraFee={extraFeeData}
                    mergeextraFee={handleDataChange}
                />
            )}

            <CancelModal
                open={showUnsavedDialog}
                handleClose={handleDontSaveAndNavigate}
                handleConfirm={handleSaveAndNavigate}
                handleCancel={handleContinueEditing}
                title={WarningUnsavedChanges.title}
                contentText={WarningUnsavedChanges.context}
                closeButtonText={WarningUnsavedChanges.closeButtonText}
                confirmButtonText={WarningUnsavedChanges.confirmButtonText}
                cancelButtonText={WarningUnsavedChanges.cancelButtonText}
                showCancelButton={true}
                isLoading={isSaving}
            />
        </ExtraFeeDetailWrapper>
    );
};

export default ExtraFeeDetail;
